"use client";

import type React from "react";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";

import { toast } from "@/utils/customToast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { axiosApiInstance } from "@/libs/axios";
import { API_ENDPOINTS } from "@/constants/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/NonPortalPopover.tsx";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import useUserStore, { selectUserTimezone } from "@/features/auth/store/useUserStore";
import timezones from "@/features/auth/components/SignUpSteps/PreferencesForm/timezones";

// Define the schema for template creation as a function that takes t
const createTemplateSchema = (t: any) =>
  z.object({
    name: z
      .string()
      .min(1, { message: t("forms:templates.validation.nameRequired") })
      .max(255, { message: t("forms:templates.validation.nameMaxLength") }),
    type: z.enum(["weekly", "daily"], {
      required_error: t("forms:templates.validation.typeRequired"),
    }),
    timezone: z
      .string({ required_error: t("forms:templates.validation.timezoneRequired") })
      .min(1, { message: t("forms:templates.validation.timezoneRequired") }),
  });

type CreateTemplateFormValues = z.infer<ReturnType<typeof createTemplateSchema>>;

interface CreateTemplateDialogProps {
  onSuccess?: () => void;
  children?: React.ReactNode;
}

export function CreateTemplateDialog({ onSuccess, children }: CreateTemplateDialogProps) {
  const { t } = useTranslation(["common", "calendar", "forms", "timezones", "auth"]);
  const userTimezone = useUserStore(selectUserTimezone);
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<CreateTemplateFormValues>({
    resolver: zodResolver(createTemplateSchema(t)),
    defaultValues: {
      name: "",
      type: "weekly",
      timezone: userTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  });

  async function onSubmit(data: CreateTemplateFormValues) {
    setIsSubmitting(true);
    try {
      await axiosApiInstance.post(API_ENDPOINTS.TUTOR.TEMPLATES.LIST, data);

      toast.success(t("calendar:templates.createSuccess"), {
        description: t("calendar:templates.templateCreated"),
      });

      setIsOpen(false);
      form.reset();
      onSuccess?.();
    } catch (error: any) {
      console.error("Failed to create template:", error);

      // Handle validation errors from the API
      if (error.response?.data?.errors) {
        const apiErrors = error.response.data.errors;

        Object.entries(apiErrors).forEach(([field, messages]) => {
          if (field === "name" || field === "type" || field === "timezone") {
            form.setError(field as "name" | "type" | "timezone", {
              type: "server",
              message: Array.isArray(messages) ? messages[0] : messages,
            });
          }
        });
      } else {
        toast.error(t("common:error"), {
          description: error.response?.data?.message || t("common:errors.genericErrorTitle"),
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {children ? (
        <DialogTrigger asChild>{children}</DialogTrigger>
      ) : (
        <DialogTrigger asChild>
          <Button variant={"foreground"}>
            <Icon className="mr-2" height={"1rem"} icon="tabler:plus" width={"1rem"} />
            {t("calendar:templates.createNew")}
          </Button>
        </DialogTrigger>
      )}

      <DialogContent aria-describedby={undefined} className="bg-background sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-accent-foreground text-xl">
            {t("calendar:templates.createNewTemplate")}
          </DialogTitle>
          <DialogDescription>{t("calendar:templates.createTemplateDescription")}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-6" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-accent-foreground">
                    {t("calendar:templates.templateName")}
                  </FormLabel>
                  <FormControl>
                    <Input
                      className="bg-foreground"
                      placeholder={t("calendar:templates.templateNamePlaceholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    {t("calendar:templates.templateNameDescription")}
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="type"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel className="text-accent-foreground">
                    {t("calendar:templates.templateType")}
                  </FormLabel>
                  <FormControl>
                    <RadioGroup
                      className="flex flex-col space-y-1"
                      defaultValue={field.value}
                      onValueChange={field.onChange}
                    >
                      <FormItem
                        className="bg-foreground hover:border-border flex cursor-pointer items-center space-y-0 rounded-md border border-transparent p-4"
                        onClick={() => field.onChange("weekly")}
                      >
                        <FormControl>
                          <RadioGroupItem
                            className="text-muted-foreground border-muted-foreground data-[state=checked]:border-accent-foreground data-[state=checked]:bg-accent-foreground data-[state=checked]:text-accent-foreground me-2"
                            value="weekly"
                          />
                        </FormControl>
                        <FormLabel className="w-full cursor-pointer font-normal">
                          <div className="flex items-center">
                            <Icon
                              className="mr-2"
                              height={"1.25rem"}
                              icon="tabler:calendar-week"
                              width={"1.25rem"}
                            />
                            <div>
                              <p className="text-accent-foreground font-medium">
                                {t("calendar:templates.weekly")}
                              </p>
                              <p className="text-muted-foreground text-sm">
                                {t("calendar:templates.weeklyDescription")}
                              </p>
                            </div>
                          </div>
                        </FormLabel>
                      </FormItem>
                      <FormItem
                        className="bg-foreground hover:border-border flex cursor-pointer items-center space-y-0 rounded-md border border-transparent p-4"
                        onClick={() => field.onChange("daily")}
                      >
                        <FormControl>
                          <RadioGroupItem
                            className="text-muted-foreground border-muted-foreground data-[state=checked]:border-accent-foreground data-[state=checked]:bg-accent-foreground data-[state=checked]:text-accent-foreground me-2"
                            value="daily"
                          />
                        </FormControl>
                        <FormLabel className="w-full cursor-pointer font-normal">
                          <div className="flex items-center">
                            <Icon
                              className="mr-2"
                              height={"1.25rem"}
                              icon="tabler:calendar"
                              width={"1.25rem"}
                            />
                            <div>
                              <p className="text-accent-foreground font-medium">
                                {t("calendar:templates.daily")}
                              </p>
                              <p className="text-muted-foreground text-sm">
                                {t("calendar:templates.dailyDescription")}
                              </p>
                            </div>
                          </div>
                        </FormLabel>
                      </FormItem>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="timezone"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel className="text-accent-foreground">
                    {t("calendar:templates.timezone")}
                  </FormLabel>
                  <FormDescription>{t("calendar:templates.timezoneDescription")}</FormDescription>
                  <Popover modal={true}>
                    <PopoverTrigger asChild>
                      <FormControl>
                        <Button
                          className="bg-foreground hover:bg-muted/30 flex w-full items-center justify-between gap-2 text-left font-normal"
                          role="combobox"
                          variant="outline"
                        >
                          <span className="flex items-center gap-2 text-sm">
                            <span className="iconify tabler--globe" />
                            {field.value
                              ? t(
                                  // @ts-ignore
                                  `timezones:${field.value}`,
                                  {
                                    defaultValue: field.value,
                                  },
                                )
                              : t("calendar:templates.timezonePlaceholder")}
                          </span>
                          <Icon className="h-4 w-4 opacity-70" icon="tabler:chevron-down" />
                        </Button>
                      </FormControl>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="p-0" side="bottom">
                      <Command>
                        <CommandInput placeholder={t("auth:signUp.searchTimezone")} />
                        <CommandList>
                          <CommandEmpty>{t("auth:signUp.noTimezoneFound")}</CommandEmpty>
                          <CommandGroup>
                            {timezones.map((timezoneOption) => (
                              <CommandItem
                                key={timezoneOption}
                                className={"text-accent-foreground"}
                                value={timezoneOption}
                                onSelect={() => {
                                  form.setValue("timezone", timezoneOption, {
                                    shouldValidate: true,
                                  });
                                }}
                              >
                                {t(
                                  // @ts-ignore
                                  `timezones:${timezoneOption}`,
                                  {
                                    defaultValue: timezoneOption,
                                  },
                                )}
                                {timezoneOption === field.value && (
                                  <Icon className="ml-auto h-4 w-4" icon="tabler:check" />
                                )}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                disabled={isSubmitting}
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
              >
                {t("common:cancel")}
              </Button>
              <Button disabled={isSubmitting} type="submit" variant={"foreground"}>
                {isSubmitting ? (
                  <div className="flex items-center">
                    <motion.div
                      animate={{ rotate: 360 }}
                      className="mr-2"
                      transition={{ duration: 1, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
                    >
                      <Icon height={"1rem"} icon="tabler:loader-2" width={"1rem"} />
                    </motion.div>
                    {t("common:creating")}
                  </div>
                ) : (
                  t("calendar:templates.createTemplate")
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
